// When is a trainee's next record audit due? (B4, Jill 2026-10-07.)
//
// The cadence is a SETTING (training_settings key audit_cadence,
// {"months": n}), never a constant here. 3 is the placeholder until the
// committee decides; the Chair changes it on the Trainee Audit page.
//
//   due = (last audit  OR  first training record  OR  when the pilot was
//          added) + months
//
// A pre-license trainee is measured against the last milestone AUDIT. A
// licensed deputy is measured against the Deputy Organizer's last
// FINAL PASS instead (deputies have no auditor). Same one rule for "licensed
// deputy" as everywhere else: src/lib/traineeKind.js.
import { isLicensedDeputy } from "./traineeKind.js";

export const DEFAULT_AUDIT_MONTHS = 3;
export const DUE_SOON_DAYS = 14;
const MIN_MONTHS = 1;
const MAX_MONTHS = 24;

// Whole months, 1..24; anything else falls back to the default.
export function normalizeMonths(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n) || n < MIN_MONTHS || n > MAX_MONTHS) return DEFAULT_AUDIT_MONTHS;
  return n;
}

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// The portal runs on Alaska time (Codex on #550).
const TZ = "America/Anchorage";
const dayInTz = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });

// A date-only string ("2026-10-08") is already a calendar day: parse its
// parts. Anything else is an instant (ISO timestamp), so it must be turned
// into the Alaska calendar day first: 2026-10-08T03:00:00Z is Oct 7 there.
// Returns a local-midnight Date for that day, or null.
export function toDay(v) {
  if (!v) return null;
  const s = String(v);
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayInTz.format(d));
  }
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

// Add whole months, clamping the day (Nov 30 + 3 months lands on Feb 28).
export function addMonths(day, months) {
  const total = day.getMonth() + months;
  const y = day.getFullYear() + Math.floor(total / 12);
  const m = ((total % 12) + 12) % 12;
  const last = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(day.getDate(), last));
}

// Newest row of a kind for a trainee. Does not assume the list is sorted.
function latestOf(verifications, traineeId, kind, position) {
  let best = null;
  for (const v of verifications || []) {
    if (v.trainee_id !== traineeId || v.kind !== kind) continue;
    if (position && v.verified_by_position !== position) continue;
    if (!best || String(v.created_at) > String(best.created_at)) best = v;
  }
  return best;
}

// Earliest valid record_date (the voyage day), not created_at: paper records
// are entered after the fact and drafts ahead of it.
function earliest(records, traineeId) {
  let best = null;
  for (const r of records || []) {
    if (r.trainee_id !== traineeId || !toDay(r.record_date)) continue;
    const d = String(r.record_date).slice(0, 10);
    if (!best || d < best) best = d;
  }
  return best;
}

// Returns { status, dueDate, daysLeft, basis, basisDate, kind }
//   status: "overdue" | "soon" (due within 14 days, today included) | "ok" | "unknown"
//   basis:  "audit" | "final_pass" (a stamp exists) | "first_record" | "added" | null
//   kind:   "audit" | "final_pass" (what the cadence is measured against)
export function auditDueFor({ trainee, verifications, records, pilotCreatedAt, months, now = new Date() }) {
  const kind = isLicensedDeputy(trainee) ? "final_pass" : "audit";
  const n = normalizeMonths(months);
  // a deputy's baseline is the Deputy Organizer's own stamp only; an old
  // Trainee Organizer final pass from before they were licensed does not
  // reset it (verified_by_position, set by record_trainee_verification)
  const last = latestOf(verifications, trainee?.id, kind, kind === "final_pass" ? "deputy_organizer" : null);
  let basis = null;
  let basisDate = null;
  if (last) { basis = kind; basisDate = toDay(last.created_at); }
  if (!basisDate) {
    const first = earliest(records, trainee?.id);
    if (first) { basis = "first_record"; basisDate = toDay(first); }
  }
  if (!basisDate && pilotCreatedAt) { basis = "added"; basisDate = toDay(pilotCreatedAt); }
  if (!basisDate) return { status: "unknown", dueDate: null, daysLeft: null, basis: null, basisDate: null, kind };

  const due = addMonths(basisDate, n);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysLeft = Math.round((due - today) / 86400000);
  const status = daysLeft < 0 ? "overdue" : daysLeft <= DUE_SOON_DAYS ? "soon" : "ok";
  return { status, dueDate: ymd(due), daysLeft, basis, basisDate: ymd(basisDate), kind };
}

// Plain words for the status line on a card.
export function auditDueLabel(d) {
  if (!d || d.status === "unknown") return "Audit due: no start date on file";
  const verb = d.kind === "final_pass" ? "Final pass" : "Audit";
  if (d.status === "overdue") {
    const n = -d.daysLeft;
    return `${verb} OVERDUE since ${d.dueDate} (${n} day${n === 1 ? "" : "s"})`;
  }
  return `${verb} due ${d.dueDate}`;
}

// Read the cadence. Never throws: a missing table (migration not applied
// yet) or a bad value falls back to the default.
export async function loadAuditMonths(supabase) {
  try {
    const { data, error } = await supabase.from("training_settings").select("value").eq("key", "audit_cadence").maybeSingle();
    if (error || !data) return DEFAULT_AUDIT_MONTHS;
    return normalizeMonths(data.value?.months);
  } catch {
    return DEFAULT_AUDIT_MONTHS;
  }
}

// Same read, but a failure gives null instead of the default, for callers
// that must not show a confident number from a guess (the menu count).
export async function loadAuditMonthsStrict(supabase) {
  try {
    const { data, error } = await supabase.from("training_settings").select("value").eq("key", "audit_cadence").maybeSingle();
    if (error) return null;
    return data ? normalizeMonths(data.value?.months) : DEFAULT_AUDIT_MONTHS;
  } catch {
    return null;
  }
}

// Chair only (the database refuses anyone else). Returns an error message or null.
export async function saveAuditMonths(supabase, months) {
  const n = normalizeMonths(months);
  const { error } = await supabase.from("training_settings")
    .upsert({ key: "audit_cadence", value: { months: n }, updated_at: new Date().toISOString() }, { onConflict: "key" });
  return error ? error.message : null;
}

// NOTE: pilots_info.created_at is live-only (the table has no definition in
// this repo; the column exists in the live DB).
// pilot id -> created_at, for the last-resort baseline. Tolerant: any error
// gives an empty map and trainees fall back to their first record.
export async function loadPilotCreated(supabase) {
  try {
    const { data, error } = await supabase.from("pilots_info").select("id, created_at");
    if (error) return {};
    return Object.fromEntries((data || []).filter((p) => p.created_at).map((p) => [p.id, p.created_at]));
  } catch {
    return {};
  }
}

// Supabase caps one select at 1000 rows. Pages through a query and returns
// { data, error } like a single select. `build` must return a fresh,
// deterministically ordered query each call.
export async function fetchAllPages(build) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999);
    if (error) return { data: out, error };
    out.push(...(data || []));
    if (!data || data.length < 1000) return { data: out, error: null };
  }
}
